import React, { useState, useEffect } from "react";
import { registerClass } from "../../api/teamApi";
import { searchInstructor } from "../../api/searchApi";
import { IoCloseSharp } from "react-icons/io5";
import Swal from "sweetalert2";

interface TeamRegistModalProps {
  isOpen: boolean;
  onClose: (state: boolean) => void;
  teamAPI: () => void;
  searchAllTeams: () => void;
  teams: ContentItem[];
}

interface ContentItem {
  id: string;
  displayName: string;
  name: string;
  deleteAt: number;
  createAt: number;
  updateAt: number;
  memberCount: number;
}

interface filteredInstructorsInfo {
  id: string;
  nickname: string;
  email: string;
}

const TeamRegistModal: React.FC<TeamRegistModalProps> = ({ isOpen, onClose, teamAPI, searchAllTeams, teams }) => {
  const [className, setClassName] = useState<string>("");
  const [classURL, setClassURL] = useState<string>("");

  const handleClassModal = () => {
    onClose(false);
    setClassName("");
    setClassURL("");
    setSelectedInstructor("");
    setChoiceInstructor("");
    searchAllTeams();
  };

  const removeChoiceInstructor = () => {
    setSelectedInstructor("");
    setChoiceInstructor("");
  };

  const handleClassURL = (classURL: string) => {
    setClassURL(classURL);
  };

  const registerCheck = (teacherUserId: string) => {
    if (className.trim() === "") {
      alert("내용을 채워주세요.");
      return;
    } else if (!isValidTeamName(classURL)) {
      return;
    }

    classRegister(teacherUserId);
  };

  // 팀 생성
  const classRegister = async (teacherUserId: string) => {
    const data = await registerClass(className, classURL, "I", teacherUserId);
    if (data) {
      teamAPI();
      setClassName("");
      setClassURL("");
      setSelectedInstructor("");
      setChoiceInstructor("");
      registTeamSuccessAlert();
      console.log(data);
    } else {
      registTeamFailAlert();
    }
  };

  // 팀 생성 유효성 체크크
  const forbiddenPrefixes = [
    "admin",
    "api",
    "channel",
    "claim",
    "error",
    "files",
    "help",
    "landing",
    "login",
    "mfa",
    "oauth",
    "plug",
    "plugins",
    "post",
    "signup",
    "playbooks",
  ];

  const isValidTeamName = (teamName: string): boolean => {
    // 정규표현식: 소문자, 숫자, 대시(-)만 포함하며, 글자로 시작 & 대시로 끝나면 안 됨, 2~64자 길이
    const regex = /^[a-z][a-z0-9-]{0,62}[a-z0-9]$/;

    // 1. 정규표현식 검증
    if (!regex.test(teamName)) {
      alert("URL 규칙을 확인해주세요.");
      return false;
    }

    // 2. 제한된 단어로 시작하는지 검사
    for (const prefix of forbiddenPrefixes) {
      if (teamName.startsWith(prefix)) {
        alert(`URL은 '${prefix}' 단어로 시작할 수 없습니다.`);
        return false;
      }
    }

    return !forbiddenPrefixes.some((prefix) => teamName.startsWith(prefix));
  };

  const [instructors, setInstructors] = useState<any>();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredInstructors, setFilteredInstructors] = useState<filteredInstructorsInfo[]>([]);
  const [filteredTeamNames, setFilteredTeamNames] = useState<ContentItem[]>([]);
  const [selectedInstructor, setSelectedInstructor] = useState("");
  const [choiceInstructor, setChoiceInstructor] = useState("");
  const [isInstructorSearch, setIsInstructorSearch] = useState<boolean>(false);
  const [isTeamNamesSearch, setIsTeamNamesSearch] = useState<boolean>(false);

  useEffect(() => {
    instructorSearch();
  }, []);

  useEffect(() => {
    setFilteredInstructors(instructors);
  }, [instructors]);

  useEffect(() => {
    setFilteredTeamNames(teams);
  }, [teams]);

  // 강사 조회
  const instructorSearch = async () => {
    const data = await searchInstructor();
    if (data) {
      console.log(data.data);
      setInstructors(data.data.content);
    }
  };

  // 팀 이름 설정
  const handleClassName = (className: string) => {
    setClassName(className);
    const filtered = teams.filter((team) => team.displayName.includes(className));
    setFilteredTeamNames(filtered);
  };

  const handleTeamNameSelect = (team: any) => {
    setClassName(team.displayName);
  };

  // 검색 키 입력 핸들러
  const handleInstructorSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (filteredInstructors.length === 1) {
        handleSelect(filteredInstructors[0]);
        setIsInstructorSearch(false);
      }
    } else if (e.key === "Escape") {
      setIsInstructorSearch(false);
    }
  };

  useEffect(() => {
    if (searchTerm) {
      setIsInstructorSearch(true);
    }
  }, [searchTerm]);

  // 검색 필터링 핸들러
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    const filtered = instructors.filter(
      (instructor) => instructor.nickname.includes(value) || instructor.email.includes(value)
    );
    setFilteredInstructors(filtered);
  };

  // 강사 선택 핸들러
  const handleSelect = (instructor: any) => {
    setChoiceInstructor(instructor.id);
    setSelectedInstructor(`${instructor.nickname} (${instructor.email})`);
    setSearchTerm("");
    setFilteredInstructors(instructors); // 검색 초기화
  };

  const registTeamSuccessAlert = () => {
    Swal.fire({
      title: "성공",
      text: "팀 생성이 성공적으로 완료되었습니다.",
      icon: "success", // success, error, warning, info, question 중 선택 가능
      confirmButtonText: "확인",
      timer: 3000, // 알림이 자동으로 사라지는 시간 (ms)
      // timerProgressBar: true, // 타이머 진행 상태를 표시
    });
    handleClassModal();
  };

  const registTeamFailAlert = () => {
    Swal.fire({
      title: "실패",
      text: "이미 존재하는 URL 입니다.",
      icon: "error", // success, error, warning, info, question 중 선택 가능
      confirmButtonText: "확인",
      timer: 3000, // 알림이 자동으로 사라지는 시간 (ms)
      // timerProgressBar: true, // 타이머 진행 상태를 표시
    });
  };

  if (!isOpen) return null;

  return (
    <div>
      {/* 밖에 누르면 다가지게 추 후 !!!!!!!! */}
      <div
        className="font-Noto_Sans_KR"
        onClick={() => {
          setIsInstructorSearch(false);
          setIsTeamNamesSearch(false);
        }}
      >
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-60 backdrop-blur-sm"></div>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="z-30 flex items-center justify-center min-h-full p-4 text-center sm:p-0">
            <div className="relative px-6 pt-6 pb-6 text-left transition-all transform bg-white shadow-2xl rounded-xl sm:my-8 sm:w-full sm:max-w-lg">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-500"
                  onClick={() => {
                    handleClassModal();
                  }}
                >
                  <span className="sr-only">닫기</span>
                  <IoCloseSharp />
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="w-full mt-3 text-left sm:mt-0 sm:text-left">
                  <h3 className="mb-8 text-2xl font-bold leading-6 text-gray-900">교육과정 등록</h3>

                  <form className="space-y-7">
                    <div>
                      <label htmlFor="course-name" className="block text-sm font-medium leading-6 text-gray-900">
                        교육과정명
                      </label>
                      <div className="relative mt-2 border black">
                        <input
                          type="text"
                          id="course-name"
                          // required=""
                          className="block w-full p-2"
                          placeholder="교육과정명을 입력하세요"
                          value={className}
                          onChange={(e) => handleClassName(e.target.value)}
                          onFocus={() => {
                            setIsInstructorSearch(false);
                            setIsTeamNamesSearch(true);
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsInstructorSearch(false);
                            setIsTeamNamesSearch(true);
                          }}
                        />

                        {/* 팀팀 목록 */}
                        {isTeamNamesSearch && (
                          <ul
                            className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border border-gray-300 rounded-lg shadow-lg max-h-40"
                            style={{ top: "100%", position: "absolute" }}
                          >
                            {filteredTeamNames.length > 0 ? (
                              filteredTeamNames.map((team) => (
                                <li
                                  key={team.id}
                                  className="p-2 cursor-pointer hover:bg-gray-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTeamNameSelect(team);
                                    setIsTeamNamesSearch(false);
                                  }}
                                >
                                  {team.displayName}
                                </li>
                              ))
                            ) : (
                              <li className="p-2 text-gray-500">검색 결과가 없습니다.</li>
                            )}
                          </ul>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="course-url" className="block text-sm font-medium leading-6 text-gray-900">
                        URL
                      </label>
                      <div className="mt-2 border black">
                        <input
                          type="url"
                          id="course-url"
                          // required=""
                          className="block w-full p-2"
                          placeholder="URL을 입력하세요"
                          value={classURL}
                          onChange={(e) => handleClassURL(e.target.value)}
                          onFocus={() => {
                            setIsInstructorSearch(false);
                            setIsTeamNamesSearch(false);
                          }}
                        />
                      </div>
                      <p className="mt-1 text-sm text-red-400">
                        {" "}
                        * 소문자, 숫자, 대시를 사용할 수 있습니다. 2~64자 사이로 입력해주세요. 글자로 시작해야 하고
                        대시로 끝나면 안 됩니다.
                      </p>
                    </div>

                    <div>
                      <label htmlFor="instructor" className="block text-sm font-medium leading-6 text-gray-900">
                        강사 검색
                      </label>
                      <div className="relative mt-2">
                        {/* 검색 입력 필드 */}
                        <input
                          id="instructor"
                          value={searchTerm}
                          onChange={handleSearch}
                          placeholder="강사 이름 또는 이메일 입력"
                          className="block w-full p-2 border border-gray-300 shadow-sm focus:border-custom focus:ring-custom sm:text-sm"
                          onFocus={() => {
                            setIsInstructorSearch(true);
                            setIsTeamNamesSearch(false);
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsInstructorSearch(true);
                            setIsTeamNamesSearch(false);
                          }}
                          onKeyDown={handleInstructorSearchKeyDown}
                        />

                        {/* 강사 목록 */}
                        {isInstructorSearch && (
                          <ul
                            className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border border-gray-300 rounded-lg shadow-lg max-h-40"
                            style={{ top: "100%", position: "absolute" }}
                          >
                            {filteredInstructors.length > 0 ? (
                              filteredInstructors.map((instructor) => (
                                <li
                                  key={instructor.id}
                                  className="p-2 cursor-pointer hover:bg-gray-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelect(instructor);
                                    setIsInstructorSearch(false);
                                  }}
                                >
                                  {instructor.nickname} ({instructor.email})
                                </li>
                              ))
                            ) : (
                              <li className="p-2 text-gray-500">검색 결과가 없습니다.</li>
                            )}
                          </ul>
                        )}

                        {/* 선택된 강사 표시 */}
                        <div>
                          <p className="mt-1 text-sm">선택된 강사</p>
                          {selectedInstructor && (
                            <div className="flex items-center justify-between my-1">
                              <p className="text-sm text-gray-700">
                                <strong>{selectedInstructor}</strong>
                              </p>
                              <button
                                type="button"
                                className="text-gray-400 hover:text-gray-500"
                                onClick={() => {
                                  removeChoiceInstructor();
                                }}
                              >
                                <span className="sr-only">닫기</span>
                                <IoCloseSharp />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              <div className="gap-3 mt-8 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => {
                    registerCheck(choiceInstructor);
                  }}
                  className="inline-flex w-full justify-center rounded-lg bg-black px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-custom/90 sm:w-auto transition-all duration-200"
                >
                  팀 생성하기
                </button>
                <button
                  onClick={() => {
                    handleClassModal();
                  }}
                  type="button"
                  className="inline-flex w-full justify-center rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto transition-all duration-200"
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamRegistModal;
